import { useMemo, useState } from "react";
import {
  FlatList,
  Modal,
  Pressable,
  StyleSheet,
  TextInput,
  View,
} from "react-native";

import type { EventGuest } from "@/utils/event-queries";

import {
  demoteMemberFromAdmin,
  promoteMemberToAdmin,
} from "@/atoms/event-members";
import { GText } from "@/components/g-text";
import { colors } from "@/constants/colors";

interface Props {
  eventId: string;
  guests: EventGuest[];
  isAdmin: boolean;
  onClose: () => void;
  visible: boolean;
}

type Tab = "going" | "mutuals";

export default function GuestListModal({
  eventId,
  guests,
  isAdmin,
  onClose,
  visible,
}: Props) {
  const [tab, setTab] = useState<Tab>("mutuals");
  const [search, setSearch] = useState("");
  const [demoteTarget, setDemoteTarget] = useState<EventGuest | null>(null);

  const adminCount = useMemo(
    () => guests.filter((g) => g.role === "admin").length,
    [guests],
  );

  const mutuals = guests.filter((g) => g.isMutual);
  const query = search.toLowerCase();

  const filtered = (tab === "mutuals" ? mutuals : guests).filter(
    (g) => !query || g.name.toLowerCase().includes(query),
  );

  function handleClose() {
    setSearch("");
    setTab("mutuals");
    onClose();
  }

  return (
    <Modal
      animationType="fade"
      onRequestClose={handleClose}
      transparent
      visible={visible}
    >
      <View style={styles.container}>
        {/* Header */}
        <View style={styles.header}>
          <Pressable onPress={handleClose} style={styles.closeButton}>
            <GText style={styles.closeText}>✕</GText>
          </Pressable>
          <GText style={styles.title}>Guest List</GText>
          <View style={styles.closeButton} />
        </View>

        {/* Search */}
        <View style={styles.searchContainer}>
          <TextInput
            onChangeText={setSearch}
            placeholder="Find a guest..."
            placeholderTextColor={colors.placeholder}
            style={styles.searchInput}
            value={search}
          />
        </View>

        {/* Tabs */}
        <View style={styles.tabRow}>
          <Pressable onPress={() => setTab("mutuals")} style={styles.tab}>
            <GText
              style={[
                styles.tabText,
                tab === "mutuals" && styles.tabTextActive,
              ]}
            >
              Mutuals {mutuals.length}
            </GText>
            {tab === "mutuals" && <View style={styles.tabIndicator} />}
          </Pressable>
          <Pressable onPress={() => setTab("going")} style={styles.tab}>
            <GText
              style={[styles.tabText, tab === "going" && styles.tabTextActive]}
            >
              Going {guests.length}
            </GText>
            {tab === "going" && <View style={styles.tabIndicator} />}
          </Pressable>
        </View>

        {/* List */}
        <FlatList
          contentContainerStyle={styles.listContent}
          data={filtered}
          keyExtractor={(item) => item.user_id}
          renderItem={({ item }) => (
            <View style={styles.guestRow}>
              <View style={styles.avatar}>
                <GText style={styles.avatarText}>
                  {(item.isSelf ? "You" : item.name).charAt(0).toUpperCase()}
                </GText>
              </View>
              <GText style={styles.guestName}>
                {item.isSelf ? "You" : item.name}
              </GText>
              {isAdmin && (
                <Pressable
                  onPress={() => {
                    switch (item.role) {
                      case "admin":
                        if (adminCount >= 2) setDemoteTarget(item);
                        break;
                      default:
                        promoteMemberToAdmin(eventId, item.user_id);
                        break;
                    }
                  }}
                  style={
                    item.role === "admin" ? undefined : styles.cohostButton
                  }
                >
                  <GText
                    style={
                      item.role === "admin"
                        ? styles.adminLabel
                        : styles.cohostText
                    }
                  >
                    {item.role === "admin" ? "host" : "make cohost"}
                  </GText>
                </Pressable>
              )}
            </View>
          )}
        />
        {/* Remove host confirmation */}
        <Modal
          animationType="fade"
          onRequestClose={() => setDemoteTarget(null)}
          transparent
          visible={demoteTarget !== null}
        >
          <Pressable
            onPress={() => setDemoteTarget(null)}
            style={styles.dialogOverlay}
          >
            <View style={styles.dialogBox}>
              <GText style={styles.dialogText}>
                remove {demoteTarget?.isSelf ? "yourself" : demoteTarget?.name}{" "}
                as host?
              </GText>
              <Pressable
                onPress={() => {
                  if (demoteTarget) {
                    demoteMemberFromAdmin(eventId, demoteTarget.user_id);
                    setDemoteTarget(null);
                  }
                }}
                style={styles.dialogConfirm}
              >
                <GText style={styles.dialogConfirmText}>yes</GText>
              </Pressable>
            </View>
          </Pressable>
        </Modal>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  adminLabel: {
    color: colors.subtle,
    fontSize: 13,
    fontWeight: "600",
  },
  avatar: {
    alignItems: "center",
    backgroundColor: colors.cardBg,
    borderRadius: 22,
    height: 44,
    justifyContent: "center",
    width: 44,
  },
  avatarText: {
    color: colors.white,
    fontSize: 18,
    fontWeight: "700",
  },
  closeButton: {
    alignItems: "center",
    height: 32,
    justifyContent: "center",
    width: 32,
  },
  closeText: {
    color: colors.white,
    fontSize: 20,
  },
  cohostButton: {
    backgroundColor: colors.cardBg,
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 6,
  },
  cohostText: {
    color: colors.white,
    fontSize: 13,
    fontWeight: "600",
  },
  container: {
    backgroundColor: colors.background,
    flex: 1,
  },
  dialogBox: {
    backgroundColor: colors.cardBg,
    borderRadius: 16,
    gap: 16,
    padding: 24,
  },
  dialogConfirm: {
    alignItems: "center",
    backgroundColor: colors.white,
    borderRadius: 10,
    paddingVertical: 12,
  },
  dialogConfirmText: {
    color: colors.background,
    fontSize: 16,
    fontWeight: "700",
  },
  dialogOverlay: {
    alignItems: "center",
    backgroundColor: "rgba(0,0,0,0.6)",
    flex: 1,
    justifyContent: "center",
    padding: 40,
  },
  dialogText: {
    color: colors.white,
    fontSize: 16,
    fontWeight: "600",
    textAlign: "center",
  },
  guestName: {
    color: colors.white,
    flex: 1,
    fontSize: 16,
    fontWeight: "600",
  },
  guestRow: {
    alignItems: "center",
    flexDirection: "row",
    gap: 14,
    paddingHorizontal: 24,
    paddingVertical: 10,
  },
  header: {
    alignItems: "center",
    flexDirection: "row",
    justifyContent: "space-between",
    paddingBottom: 16,
    paddingHorizontal: 20,
    paddingTop: 60,
  },
  listContent: {
    paddingBottom: 40,
  },
  searchContainer: {
    paddingBottom: 12,
    paddingHorizontal: 20,
  },
  searchInput: {
    backgroundColor: colors.cardBg,
    borderRadius: 12,
    color: colors.white,
    fontSize: 16,
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  tab: {
    alignItems: "center",
    flex: 1,
    paddingBottom: 10,
  },
  tabIndicator: {
    backgroundColor: colors.white,
    borderRadius: 1,
    bottom: 0,
    height: 2,
    left: 0,
    position: "absolute",
    right: 0,
  },
  tabRow: {
    flexDirection: "row",
    marginBottom: 8,
    paddingHorizontal: 20,
  },
  tabText: {
    color: colors.subtle,
    fontSize: 15,
    fontWeight: "600",
  },
  tabTextActive: {
    color: colors.white,
  },
  title: {
    color: colors.white,
    fontSize: 18,
    fontWeight: "700",
  },
});
